import React from 'react';
import { OpForm } from 'components/customAntd/DLS/OpForm/OpForm';
import { VisitorWorkflow, VisitorWorkflowType } from 'types/visitorWorkflowTypes';
import { useTranslation } from 'react-i18next';
import { VisitorType } from 'types/VisitorTypeTypes';
import { Divider, Form, List } from 'antd';
import { PlusOutlined, DeleteOutlined } from '@ant-design/icons';
import { OpButton } from "components/customAntd/DLS/OpButton/OpButton";
import { useSelector } from 'react-redux';
import { RootState } from 'store/store';
import { hasPermission } from 'utils/utils';

interface WorkflowFormProps {
  form: any;
  visitorWorkflows: VisitorWorkflow[];
  workflowTypes: VisitorWorkflowType[];
  visitorTypes: VisitorType[];
}

const WorkflowForm: React.FC<WorkflowFormProps> = ({ form, visitorWorkflows, workflowTypes, visitorTypes }) => {
  const orgId = useSelector((state: RootState) => state.globalOrg.globalOrgId);
  const tokenScopeList = useSelector((state: RootState) => state.auth.auth.data[0]?.tokenScopeList || []);
  const isDarkMode = useSelector((state: RootState) => state.theme.isDarkMode);

  const hasLocationWrite = hasPermission(tokenScopeList, orgId, 'o', 'location:w');
  const { t } = useTranslation();

  const formValues = (OpForm.useWatch([], form) || {}) as any;

  const getFilteredVisitorTypeOptions = (rowIndex: number, workflowTypeId: string, visitorTypeOptions: { label: string; value: number }[]) => {
    const listValues = formValues[workflowTypeId] || [];

    const selectedIds = new Set(
      listValues.flatMap((row: any, index: number) =>
        index === rowIndex ? [] : row?.visitorTypeId || []
      )
    );

    return visitorTypeOptions.filter((option) => !selectedIds.has(option.value));
  };

  const getFilteredWorkflowOptions = (rowIndex: number, workflowTypeId: string, workflowOptions: { label: string; value: number }[]) => {
    const listValues = formValues[workflowTypeId] || [];

    const selectedIds = new Set(
      listValues.map((row: any, index: number) =>
        index === rowIndex ? null : row?.workflowId
      ).filter((id: number | null) => id != null)
    );

    return workflowOptions.filter((option) => !selectedIds.has(option.value));
  };

  const getFormFieldName = (name: string) => {
    switch (name) {
      case 'Attendant Sign In': return 'SignInWorkflowId';
      case 'Attendant Sign Out': return 'SignOutWorkflowId';
      case 'Kiosk Sign In': return 'KioskInWorkflowId';
      case 'Kiosk Sign Out': return 'KioskOutWorkflowId';
    }
  };

  return (
    <>
      {workflowTypes.map((type) => {
        if (!type.languageCode) return null;

        const workflowOptions = visitorWorkflows
          .filter(w => w.workflowTypeId === type.id)
          .map(w => ({ label: w.name ?? '', value: w.id }));

        const visitorTypeOptions = visitorTypes
          .map(v => ({ label: v.name ?? '', value: v.id }));

        return (
          <div key={type.id}>
            <OpForm.Select
              label={t(type.languageCode, type.name ?? '')}
              name={getFormFieldName(type.name || '')}
              options={workflowOptions}
            />
            <Form.List name={`${type.id}`}>
              {(values, { add, remove }) => (
                <>
                  {values.length > 0 && (
                    <List
                      locale={{ emptyText: ' ' }}
                      dataSource={values}
                      bordered
                      style={{ background: isDarkMode ? '#121826' : undefined }}
                      renderItem={(value, index) => (
                        <List.Item
                          style={{ position: 'relative', gap: '12px' }}
                        >
                          <div style={{ flex: 1 }}>
                            <OpForm.Select
                              label={t('location.workflow.visitorType')}
                              name={[value.name, 'visitorTypeId']}
                              options={getFilteredVisitorTypeOptions(index, `${type.id}`, visitorTypeOptions)}
                              mode="multiple"
                            />
                          </div>
                          <div style={{ flex: 1 }}>
                            <OpForm.Select
                              label={t('location.workflow.workflow')}
                              name={[value.name, 'workflowId']}
                              options={getFilteredWorkflowOptions(index, `${type.id}`, workflowOptions)}
                            />
                          </div>
                          {hasLocationWrite && (
                            <DeleteOutlined
                              onClick={() => { remove(value.name); }}
                              style={{ position: 'absolute', top: 15, right: 15, color: 'red', cursor: 'pointer' }}
                            />
                          )}
                        </List.Item>
                      )} />
                  )}
                  {hasLocationWrite && (
                    <OpButton type="dashed" onClick={() => add()} style={{ marginTop: '8px', width: 'auto' }} block icon={<PlusOutlined />}>
                      {t('location.workflow.specifyByType')}
                    </OpButton>
                  )}
                </>
              )}
            </Form.List>
            <Divider />
          </div>
        );
      })}
    </>
  );
};

export default WorkflowForm;
